import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {memo, useEffect, useMemo, useRef, useState} from 'react';
import {useTrans} from '@ui/i18n/use-trans';
import {message} from '@ui/i18n/message';

// Country-name overrides keyed by LOCALE: keeps the English / Turkish hand-
// picked forms while letting Arabic (and any other locale) fall through to
// `Intl.DisplayNames` so it renders "تركيا" instead of the English "Türkiye".
// On some devices (notably Apple/Safari CLDR data) Intl.DisplayNames renders
// CN as "China mainland", TW as "Taiwan, Province of China", HK/MO as "…SAR
// China". Force the common short forms per locale so the label is stable across
// devices.
const NAME_OVERRIDES: Record<string, Record<string, string>> = {
  en: {TR: 'Türkiye', CZ: 'Czechia', SZ: 'Eswatini', MM: 'Myanmar', CN: 'China', TW: 'Taiwan', HK: 'Hong Kong', MO: 'Macao'},
  tr: {TR: 'Türkiye', CZ: 'Çekya', CN: 'Çin', TW: 'Tayvan', HK: 'Hong Kong', MO: 'Makao'},
  ar: {CN: 'الصين', TW: 'تايوان', HK: 'هونغ كونغ', MO: 'ماكاو'},
};

/** Read the live <html lang> set by the i18n switcher so callers don't
 *  need to thread the locale through every chip. */
function currentLocale(): string {
  if (typeof document === 'undefined') return 'en';
  const l = (document.documentElement.lang || '').toLowerCase();
  return l === 'ar' || l === 'tr' ? l : 'en';
}

export function displayCountryName(
  code: string | undefined | null,
  fallbackName?: string | null,
  locale?: string,
): string {
  if (!code) return fallbackName || '';
  const upper = code.toUpperCase();
  const loc = locale || currentLocale();
  if (NAME_OVERRIDES[loc]?.[upper]) return NAME_OVERRIDES[loc][upper];
  try {
    const names = new Intl.DisplayNames([loc], {type: 'region'});
    const n = names.of(upper);
    if (n) return n;
  } catch {}
  return fallbackName || upper;
}

interface CountryFlagProps {
  code: string;
  size?: string;
  className?: string;
}
export const CountryFlag = memo(function CountryFlag({
  code,
  size = 'h-14 w-20',
  className = '',
}: CountryFlagProps) {
  const baseUrl = getBootstrapData().settings.base_url || '';
  return (
    <img
      src={`${baseUrl}/flags/${code.toLowerCase()}.svg`}
      className={`${size} shrink-0 rounded border border-divider object-cover shadow-sm ${className}`}
      alt={code}
      loading="lazy"
    />
  );
});

interface CountryLabelProps {
  code?: string | null;
  name?: string | null;
  size?: string;
  className?: string;
}
export function CountryLabel({code, name, size, className = ''}: CountryLabelProps) {
  if (!code && !name) return <>-</>;
  return (
    <span className={`inline-flex items-center gap-6 ${className}`}>
      {code ? <CountryFlag code={code} size={size} /> : null}
      <span>{displayCountryName(code || '', name)}</span>
    </span>
  );
}

// Strip diacritics so "turkiye" matches "Türkiye" etc.
function fold(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export interface CountryOption {
  id: string | number;
  code: string;
  name?: string | null;
  count?: number;
}

interface CountrySelectProps {
  countries: CountryOption[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  className?: string;
}
/**
 * Searchable country picker with flag and override-aware names. Lightweight
 * (no React Aria) — designed for filter sidebars where ComboBox is overkill.
 */
export function CountrySelect({
  countries,
  value,
  onChange,
  placeholder = 'All Countries',
  className = '',
}: CountrySelectProps) {
  const {trans} = useTrans();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const placeholderText = trans(message(placeholder));

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selected = useMemo(
    () => countries.find(c => String(c.id) === String(value)),
    [countries, value],
  );

  const filtered = useMemo(() => {
    if (!query) return countries;
    const q = fold(query);
    return countries.filter(c => {
      const display = displayCountryName(c.code, c.name);
      return (
        fold(display).includes(q) ||
        fold(c.code).includes(q) ||
        (c.name ? fold(c.name).includes(q) : false)
      );
    });
  }, [countries, query]);

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center justify-between gap-6 rounded-lg border bg-paper px-8 py-6 text-left text-sm hover:bg-hover"
      >
        {selected ? (
          <span className="flex min-w-0 items-center gap-6">
            <CountryFlag code={selected.code} size="h-12 w-18" />
            <span className="truncate">
              {displayCountryName(selected.code, selected.name)}
            </span>
          </span>
        ) : (
          <span className="text-muted">{placeholderText}</span>
        )}
        <span className="text-muted">▾</span>
      </button>
      {open && (
        <div className="absolute z-30 mt-4 max-h-280 w-full overflow-y-auto rounded-lg border bg-paper opacity-100 shadow-lg" style={{backgroundColor: 'var(--be-paper, #fff)'}}>
          <input
            autoFocus
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={trans(message('Search countries...'))}
            className="sticky top-0 z-10 w-full border-b bg-paper px-10 py-6 text-sm outline-none"
            style={{backgroundColor: 'var(--be-paper, #fff)'}}
          />
          <button
            type="button"
            onClick={() => {
              onChange('');
              setOpen(false);
              setQuery('');
            }}
            className="flex w-full items-center gap-6 px-10 py-6 text-left text-sm text-muted hover:bg-hover"
          >
            {placeholderText}
          </button>
          {filtered.map(c => (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                onChange(String(c.id));
                setOpen(false);
                setQuery('');
              }}
              className={`flex w-full items-center gap-6 px-10 py-6 text-left text-sm hover:bg-hover ${
                String(c.id) === String(value) ? 'bg-hover' : ''
              }`}
            >
              <CountryFlag code={c.code} size="h-12 w-18" />
              <span className="truncate">
                {displayCountryName(c.code, c.name)}
              </span>
              {c.count != null && (
                <span className="ms-auto shrink-0 text-xs text-muted">({c.count.toLocaleString()})</span>
              )}
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="px-10 py-12 text-center text-xs text-muted">
              {trans(message('No countries match'))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
